/**
 * A COMPLETED week's lineup, rendered from what the engine scored -- never from today's roster.
 *
 * ── THE BUG THIS EXISTS FOR ─────────────────────────────────────────────────
 *
 * Matchup detail and My Team built every week's lineup from the CURRENT `nfl_rosters` slots.
 * League of Trash wk3, "milk truk just arive": the stored score 57.7 is exactly the week-3
 * starters (Geno Smith, Keenan Allen, Etienne, KC D/ST, ...), but the UI showed Hurts, Kamara
 * and Downs -- benched that week, in today's QB/RB1/FLEX -- and omitted the real starters,
 * summing to ~85. Scoring was right; the page contradicted the record.
 *
 * ── THE SOURCE ──────────────────────────────────────────────────────────────
 *
 * `nfl_player_match_scores` for that team and week, including players dropped since. Starter
 * = `is_starter`. Slot = `position_slot`, whose `position_slot_source` says what it is:
 *
 *   'engine'   written at scoring time -- a RECORD.
 *   'derived'  reconstructed afterwards (weeks scored before the column existed) -- NOT a
 *              record. Any week carrying one must say "slots reconstructed".
 *   null       no slot yet. Derived here, on the fly, and flagged exactly like 'derived'.
 *
 * ── WHAT IS NEVER DONE ──────────────────────────────────────────────────────
 *
 *   - No fallback to the current roster. A week with no rows is "lineup not recorded".
 *   - No hiding. A starter the engine counted but who fits no slot (the stale 0.00 extras of
 *     SAB-1226, up to 14 starters for 11 slots) is listed as "also counted", so the rows on
 *     screen still sum to the stored total.
 *
 * ── ARITHMETIC ──────────────────────────────────────────────────────────────
 *
 * In integer HUNDREDTHS. Scores are NUMERIC(.,2); a JS float sum of 4.55 + ... drifts, and
 * `toFixed(1)` rounds 4.55 to "4.5" (SAB-1227). Cents are exact, and rounding cents to tenths
 * half-away-from-zero is exactly Postgres `round(numeric, 1)`.
 */

import { canPlaySlotWith, type SlotEligibilityRow } from '@/lib/nflStartablePositions';
import { isBenchSlot, isIrSlot, isStarterSlot, slotBase } from '@/lib/nflRosterSlots';

/* ── cents ─────────────────────────────────────────────────────────────────── */

/** A NUMERIC(.,2) value (number or numeric string) as integer hundredths. */
export function toCents(v: number | string | null | undefined): number {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
}

/** Postgres `round(x, 1)` on a value given in hundredths: half away from zero. */
export function centsToTenths(cents: number): number {
  const sign = cents < 0 ? -1 : 1;
  return (sign * Math.floor((Math.abs(cents) + 5) / 10)) / 10;
}

/** "+4.55" / "-0.15" -- exact 2 dp, signed. */
export function fmtCents2(cents: number): string {
  const sign = cents < 0 ? '-' : '+';
  const a = Math.abs(cents);
  return `${sign}${Math.floor(a / 100)}.${String(a % 100).padStart(2, '0')}`;
}

/**
 * The text a COMPLETED week's row shows: the STORED value, exactly, to 2 dp.
 *
 * Deliberately NOT the 1-dp formatter used everywhere else. Decided 2026-10-12 (Adam): with
 * 1 dp, the starter rows of 181 of 292 completed team-weeks on prod do not sum to the stored
 * header (up to 0.3 off) -- a visible "rows don't add up", the shape of the milk truk complaint.
 * At 2 dp every row is the record and their exact sum rounds to the header on 290 of 292 (the
 * other two are the engine's own wrong-direction rounding, SAB-1227). Live-week rows and every
 * other surface stay at 1 dp. Both past-week surfaces (matchup detail, My Team) call THIS, so a
 * regression to 1 dp has one place to happen and one test to catch it.
 */
export function pastWeekScoreText(cents: number): string {
  return fmtCents2(cents);
}

/** "+4.6" -- 1 dp, rounded the way Postgres rounds. */
export function fmtCents1(cents: number): string {
  const t = centsToTenths(cents);
  const s = Math.abs(t).toFixed(1);  // t is an exact tenth by construction; toFixed only pads
  return `${t < 0 ? '-' : '+'}${s}`;
}

/* ── rows ──────────────────────────────────────────────────────────────────── */

export type SlotSource = 'engine' | 'derived' | null;

export interface WeekScoreRow {
  id: string;
  player_id: string;
  /** nfl_players.position -- what slot eligibility is judged on. */
  position: string | null;
  is_starter: boolean;
  position_slot: string | null;
  position_slot_source: SlotSource;
  trash_points: number | string | null;
  anti_points: number | string | null;
  /** The CARD BONUS, not the player total (SAB-809). Total = trash + anti + this. */
  sabotage_points: number | string | null;
  /** For a stable tie-break only. */
  full_name?: string | null;
}

export function rowCents(r: Pick<WeekScoreRow, 'trash_points' | 'anti_points' | 'sabotage_points'>): number {
  return toCents(r.trash_points) + toCents(r.anti_points) + toCents(r.sabotage_points);
}

const byWeight = (a: WeekScoreRow, b: WeekScoreRow) =>
  Math.abs(rowCents(b)) - Math.abs(rowCents(a))
  || String(a.full_name ?? '').localeCompare(String(b.full_name ?? ''))
  || a.id.localeCompare(b.id);

/* ── derivation (a reconstruction; callers must mark it) ──────────────────── */

/**
 * Place starters into the league's starter slots when the slot was never recorded.
 *
 * Deterministic: slots are filled most-restrictive first (fewest eligible positions -- QB, K,
 * P, DST, COACH before RB/WR/TE before FLEX/IDP), each taking the eligible unplaced starter
 * with the largest |points|. So a 0.00 stale extra is placed only if nothing else fits, and is
 * otherwise left unplaced (null) to be shown as "also counted". Bench rows get 'BN' -- their
 * bench number was never recorded and is not guessed.
 *
 * Eligibility rows are REQUIRED: unlike the lineup pre-check, an unloaded table must not
 * permit everything here, because a reconstruction that ignores eligibility would put a TE
 * at QB and present it as the lineup.
 */
export function deriveSlots(
  rows: WeekScoreRow[],
  slotOrder: readonly string[],
  eligibility: SlotEligibilityRow[],
): Map<string, string | null> {
  if (!eligibility || eligibility.length === 0) {
    throw new Error('deriveSlots: slot eligibility rows are required');
  }
  const out = new Map<string, string | null>();
  const flexibility = (slot: string) => eligibility.filter((e) => e.slot_base === slotBase(slot)).length;
  const starterSlots = slotOrder
    .filter(isStarterSlot)
    .map((slot, i) => ({ slot, i, f: flexibility(slot) }))
    .sort((a, b) => a.f - b.f || a.i - b.i)
    .map((s) => s.slot);

  const starters = rows.filter((r) => r.is_starter).sort(byWeight);
  const placed = new Set<string>();
  for (const slot of starterSlots) {
    const pick = starters.find((r) => !placed.has(r.id) && !!r.position && canPlaySlotWith(eligibility, r.position, slot));
    if (pick) { placed.add(pick.id); out.set(pick.id, slot); }
  }
  for (const r of starters) if (!placed.has(r.id)) out.set(r.id, null);
  for (const r of rows) if (!r.is_starter) out.set(r.id, 'BN');
  return out;
}

/* ── the lineup a completed week renders ──────────────────────────────────── */

export type PastWeekLineup =
  | { status: 'not-recorded' }
  | {
      status: 'recorded';
      /** Every starter slot of the league, in order, with the row that held it (or null). */
      slots: { slot: string; row: WeekScoreRow | null }[];
      /** Starters the engine counted that hold no slot here (stale extras, duplicate slots). */
      alsoCounted: WeekScoreRow[];
      bench: WeekScoreRow[];
      /** True if ANY slot shown is a reconstruction -> "slots reconstructed" must be shown. */
      hasDerived: boolean;
      /** Exact sum of every starter row (slotted + also counted), in hundredths. */
      starterCents: number;
    };

/**
 * Build the completed week's lineup from its score rows. `eligibility` is used only when some
 * rows carry no slot yet (pre-backfill); then the whole week is derived and flagged.
 */
export function buildPastWeekLineup(
  rows: WeekScoreRow[],
  slotOrder: readonly string[],
  eligibility: SlotEligibilityRow[] | null,
): PastWeekLineup {
  if (rows.length === 0) return { status: 'not-recorded' };

  let effective = rows;
  let hasDerived = rows.some((r) => r.position_slot_source === 'derived');
  // Any row with no slot at all -> reconstruct the week here (same rule as the backfill) and
  // flag it. Never silently: a slot shown without a source would read as a record.
  if (rows.some((r) => r.position_slot_source === null) && eligibility && eligibility.length > 0) {
    const d = deriveSlots(rows, slotOrder, eligibility);
    effective = rows.map((r) => (r.position_slot_source === null
      ? { ...r, position_slot: d.get(r.id) ?? null, position_slot_source: 'derived' as const }
      : r));
    hasDerived = true;
  } else if (rows.some((r) => r.position_slot_source === null)) {
    // No eligibility to reconstruct with: unslotted starters fall to "also counted"; still
    // flagged, since the lineup shown is not the recorded one.
    hasDerived = true;
  }

  const starterSlots = slotOrder.filter(isStarterSlot);
  const starters = effective.filter((r) => r.is_starter).sort(byWeight);
  const used = new Set<string>();
  const slots = starterSlots.map((slot) => {
    const row = starters.find((r) => !used.has(r.id) && r.position_slot === slot) ?? null;
    if (row) used.add(row.id);
    return { slot, row };
  });
  // Unslotted, duplicate-slot (SAB-1226: a dropped starter and his replacement both 'WR2'),
  // or a slot this league no longer has -- all still counted by the engine, all shown.
  const alsoCounted = starters.filter((r) => !used.has(r.id));

  const slotIndex = (s: string | null) => {
    const i = s ? slotOrder.indexOf(s) : -1;
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  };
  const bench = effective
    .filter((r) => !r.is_starter)
    .sort((a, b) => slotIndex(a.position_slot) - slotIndex(b.position_slot)
      || (isIrSlot(a.position_slot ?? '') ? 1 : 0) - (isIrSlot(b.position_slot ?? '') ? 1 : 0)
      || byWeight(a, b));

  const starterCents = starters.reduce((s, r) => s + rowCents(r), 0);
  return { status: 'recorded', slots, alsoCounted, bench, hasDerived, starterCents };
}

/** For a bench row label: 'BN3' / 'IR1' as recorded, or 'BN' when only derived. */
export function benchLabel(r: WeekScoreRow): string {
  const s = r.position_slot;
  return s && (isBenchSlot(s) || isIrSlot(s)) ? s : 'BN';
}
